import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  AddressAutocompleteInput,
  Alert,
  ApiError,
  Checkbox,
  FieldLabel,
  Input,
  Select,
  Spinner,
  LINES_OF_AUTHORITY,
  MESSAGES,
  PHARMACY_PARTNER_TERMS_URL,
  StateLicensesEditor,
  TermsAgreementLabel,
  US_STATES,
  checkZipCityState,
  cn,
  fmtDate,
  formatUsPhone,
  normalizeZip,
  validateNpi,
  validateZip,
  zipMismatchMessage,
  type AddressSuggestion,
  type EntityType,
  type LineOfAuthority,
  type NpiVerification,
  type OrgType,
  type PartnerDocumentStatus,
  type PartnerNppesLookupResponse,
  type PartnerOrgProfileDraftInput,
  type PartnerOrgProfileInput,
  type PartnerOrgProfileResponse,
  type StateLicense,
} from "@dearbill/shared";
import { CheckCircleIcon } from "@dearbill/shared/components/icons";
import { useApi } from "../auth/useApi";
import { ConfirmModal } from "../components/ConfirmModal";
import {
  formatApiErrorDetail,
  validateLegalName,
  validateNpn,
  validateStateLicenses,
  validateTaxId,
} from "./orgProfileValidation";

const ENTITY_TYPES: { value: EntityType; label: string }[] = [
  { value: "Corporation (Inc)", label: "Corporation (Inc)" },
  { value: "Limited Liability Company (LLC)", label: "Limited Liability Company (LLC)" },
  { value: "Sole Proprietorship", label: "Sole Proprietorship" },
  { value: "General Partnership", label: "General Partnership" },
];

/** The strict PUT's 422 detail when a Pharmacy or Broker org submits without the terms box. */
const TERMS_REQUIRED_DETAIL = "termsAccepted is required";

/** Broker licensed-states validation result; `rows` is index-aligned. */
type LicenseErrors = { list: string | null; rows: (string | null)[] };
const NO_LICENSE_ERRORS: LicenseErrors = { list: null, rows: [] };

/** Editable form state: the PUT body, flattened for plain inputs. */
interface FormState {
  legalName: string;
  entityType: EntityType | "";
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  npi: string;
  dea: string;
  /** Tax ID split into its two digit boxes; combined as `prefix-suffix` on the wire. */
  taxIdPrefix: string;
  taxIdSuffix: string;
  /** Broker credentials; blank or empty for other org types. */
  npn: string;
  lineOfAuthority: LineOfAuthority | "";
  stateLicenses: StateLicense[];
}

/**
 * Combine the two tax-ID boxes into the wire string: `""` when both are empty,
 * else `prefix-suffix`, so a partially filled state round-trips through the
 * draft autosave and splits back cleanly on reload.
 */
function combineTaxId(prefix: string, suffix: string): string {
  return prefix === "" && suffix === "" ? "" : `${prefix}-${suffix}`;
}

function toFormState(res: PartnerOrgProfileResponse): FormState {
  const { profile } = res;
  // Split the stored tax ID on the first "-". A value with no dash, which the
  // admin edit path allows, fills the prefix box.
  const taxId = profile.taxId;
  const dash = taxId.indexOf("-");
  return {
    legalName: profile.legalName,
    entityType: profile.entityType ?? "",
    street1: profile.address.street1,
    street2: profile.address.street2,
    city: profile.address.city,
    state: profile.address.state,
    zip: profile.address.zip,
    npi: profile.npi,
    dea: profile.dea,
    taxIdPrefix: dash === -1 ? taxId : taxId.slice(0, dash),
    taxIdSuffix: dash === -1 ? "" : taxId.slice(dash + 1),
    npn: profile.npn,
    lineOfAuthority: profile.lineOfAuthority ?? "",
    stateLicenses: profile.stateLicenses,
  };
}

/**
 * The lenient draft-autosave body: the raw form state.
 *
 * The server accepts blank and malformed values without a 422. The only
 * mappings are empty entity-type and line-of-authority selects (`""`), sent as
 * `null`, and the combined tax ID.
 */
function toDraftBody(f: FormState): PartnerOrgProfileDraftInput {
  return {
    legalName: f.legalName,
    address: { street1: f.street1, street2: f.street2, city: f.city, state: f.state, zip: f.zip },
    entityType: f.entityType === "" ? null : f.entityType,
    npi: f.npi,
    dea: f.dea,
    taxId: combineTaxId(f.taxIdPrefix, f.taxIdSuffix),
    npn: f.npn,
    stateLicenses: f.stateLicenses,
    lineOfAuthority: f.lineOfAuthority === "" ? null : f.lineOfAuthority,
  };
}

/** Trailing debounce for the draft autosave: a PUT fires this long after the last edit. */
const AUTOSAVE_DEBOUNCE_MS = 2000;

/** Classes modeled on the shared `Button` `secondary` variant, for this screen's primary actions. */
const BTN_PRIMARY =
  "bg-deep-navy inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-lg px-6 py-3.5 text-base font-bold leading-[1.5] text-white transition hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40";

/** Classes modeled on the shared `Button` `outlined` variant, for secondary actions. */
const BTN_SECONDARY =
  "border-ember text-ember hover:bg-ember/7 inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-lg border-2 bg-transparent px-5 py-3 text-[15px] font-bold leading-[1.5] transition disabled:cursor-not-allowed disabled:opacity-40";

const CARD = "border-border-strong rounded-2xl border bg-white p-6";

const CARD_HEADING = "text-foreground font-serif text-[21px] font-semibold leading-7";

const STATUS_SUCCESS =
  "border-success-border bg-success-bg text-success rounded-lg border px-4 py-3 text-sm";

const STATUS_WARN = "border-haze bg-lemon text-ochre rounded-lg border px-4 py-3 text-sm";

/**
 * Classes for the two tax-ID boxes: a copy of the shared `Input`'s classes
 * without `w-full`, because the boxes are sized to their digit count. The call
 * site adds the error ring with `cn`.
 */
const TAX_ID_BOX_CLASSES =
  "rounded-2xl border-2 border-border bg-input-bg px-4 py-3.5 text-[17px] leading-[1.5] text-foreground placeholder:text-placeholder focus:border-primary focus:bg-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:text-cloud-blue";

interface OrganizationTabProps {
  orgId: string;
  res: PartnerOrgProfileResponse;
  /** Lift the server's updated response so the shell's badge/banner re-render after save. */
  onSaved: (next: PartnerOrgProfileResponse) => void;
}

/**
 * Organization tab of the org page: the read-only summary for an active org,
 * and the profile form for everything else.
 *
 * An active (approved) org lands on the summary state of the design (Figma
 * dR8cJ6hRSW5eERToo64boe node 2340:769): the org card with the
 * approved-to-refer marker, NPI or broker credentials, address and phone, the
 * Documents on file card, and the Owner card. Its Edit button (an org_admin;
 * "View details" for a `user`) switches to the form below. An org that isn't
 * active yet lands in the form directly.
 *
 * In the form, `org_admin` members get editable fields; `user` members see a
 * read-only view with no Submit button. The org's phone and email come from
 * its owner and are not on the form. A Pharmacy or Broker org's form ends with
 * the Terms & Conditions checkbox, which keeps Submit disabled until ticked;
 * once the org has accepted, the box shows ticked and disabled with the
 * acceptance date. Saves go out two ways:
 *   - Draft autosave: every edit reschedules a trailing debounce
 *     (`AUTOSAVE_DEBOUNCE_MS`) that PUTs the raw form state to the lenient
 *     `/profile/draft` endpoint, which accepts blank or malformed values and
 *     runs no NPPES lookup. A pending edit is flushed, fire-and-forget, on
 *     unmount.
 *   - Submit: client validation, then the review modal, then one strict
 *     `PUT /profile`, which validates the credentials, runs the NPPES lookup
 *     for an NPI, records a pending Terms & Conditions acceptance and
 *     completes the profile. Submit cancels the pending debounce and waits out
 *     any in-flight draft, so the two PUTs never race.
 *
 * The component owns its form and save state; the shell owns the single
 * profile fetch and passes the response in. A successful draft or Submit lifts
 * the server's response through `onSaved`, so the shared status badge and
 * banner re-render.
 */
export function OrganizationTab({ orgId, res, onSaved }: OrganizationTabProps): ReactNode {
  const api = useApi();

  // Summary-vs-form split: an active org lands on the read-only summary, and
  // the summary's Edit button flips to the form; everyone else (incomplete,
  // pending, under review) lands in the form directly. `editing` survives a
  // save, so a mid-edit `onSaved` lift doesn't bounce the admin back.
  const [editing, setEditing] = useState(false);
  const showSummary = res.status === "active" && !editing;

  // Smarty US Autocomplete Pro embedded key. When it is empty, the street-address
  // field is a plain input with no lookups. It is read at render time, not at
  // module scope, so tests can stub the env.
  const smartyKey = import.meta.env.VITE_SMARTY_EMBEDDED_KEY ?? "";

  const [form, setForm] = useState<FormState>(() => toFormState(res));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [legalNameError, setLegalNameError] = useState<string | null>(null);
  const [npiError, setNpiError] = useState<string | null>(null);
  const [zipError, setZipError] = useState<string | null>(null);
  const [taxIdError, setTaxIdError] = useState<string | null>(null);
  // Broker credential errors: the NPN, and the licensed-states list and rows.
  const [npnError, setNpnError] = useState<string | null>(null);
  const [licenseErrors, setLicenseErrors] = useState<LicenseErrors>(NO_LICENSE_ERRORS);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  // Pre-save review prompt: an informational "make sure your info is correct"
  // dialog shown after client validation passes (see onSubmit), never on the
  // raw button click.
  const [confirmOpen, setConfirmOpen] = useState(false);
  // The Terms & Conditions box. It only gates Submit and rides the strict PUT;
  // the draft autosave never carries it.
  const [termsTicked, setTermsTicked] = useState(false);
  // Non-blocking NPI-registry verdict, seeded from the GET so the existing
  // verdict shows on first load, and refreshed from each PUT response.
  const [npiVerification, setNpiVerification] = useState<NpiVerification>(res.npiVerification);
  // NPPES prefill: the Fetch button beside a valid NPI looks the number up in
  // the registry and overwrites the legal name, and the address when the record
  // has one. The message is a non-blocking notice (outage, no match,
  // individual NPI) and never gates the form.
  const [nppesFetching, setNppesFetching] = useState(false);
  const [nppesFetchMessage, setNppesFetchMessage] = useState<string | null>(null);
  // True while a draft PUT is in flight; drives the "Saving…" status next to Submit.
  const [autosaving, setAutosaving] = useState(false);
  // Set when a draft PUT 409s because the profile locked mid-session. It
  // freezes the form and stops autosaving without a refetch, and shows the
  // same banner as `profileLocked`.
  const [remoteLocked, setRemoteLocked] = useState(false);

  const canEdit = res.role === "org_admin";
  // Profile-edit lock: an org-admin can normally edit, but a locked profile is
  // read-only (the server PUT 409s). The terms box stays gated on `canEdit`,
  // not this, so a locked form still shows the acceptance.
  const locked = res.profileLocked || remoteLocked;
  const formEditable = canEdit && !locked;
  // The organization type is chosen at creation and immutable, so it is read
  // off the server response rather than held as form state.
  const orgType: OrgType = res.profile.organizationType;
  const isBroker = orgType === "Broker";

  // Terms & Conditions, for Pharmacy and Broker orgs (`agreementRequired`).
  // `blockers` lists `document_unsigned` exactly while the acceptance is
  // missing, so the accepted state needs no fetch; only its date does, and the
  // document endpoint is org-admin only. The summary's Documents card reads the
  // same fetch.
  const termsAccepted = res.agreementRequired && !res.blockers.includes("document_unsigned");
  const termsOwed = res.agreementRequired && !termsAccepted;
  const { doc: termsDoc, loadError: termsDocError } = useTermsAcceptance(
    orgId,
    canEdit && termsAccepted,
  );

  // Draft-autosave machinery. Refs, not state, because the debounce timer and
  // the PUT's resolution run outside React's render cycle and must see current
  // values:
  //   - formRef mirrors `form` so a draft always sends the latest edits;
  //   - editSeqRef counts edits; a draft captures it at dispatch and compares
  //     on resolve, so a stale echo never clobbers newer keystrokes;
  //   - savedSeqRef is the last successfully saved edit count; when it equals
  //     editSeqRef, nothing is left to save;
  //   - inFlightRef, queuedRef and draftPromiseRef serialise draft PUTs: never
  //     two in flight, and Submit awaits draftPromiseRef before the strict PUT;
  //   - timerRef is the pending debounce, cancelled on Submit and unmount;
  //   - lockedRef mirrors `remoteLocked` synchronously, since a 409 can land
  //     between renders; submittingRef blocks drafts while the strict PUT runs;
  //   - draftErrorRef marks that saveError came from a draft, so only a draft
  //     success clears it, never a Submit validation message;
  //   - flushRef holds the latest `saveDraft` closure for the unmount flush.
  const formRef = useRef(form);
  const editSeqRef = useRef(0);
  const savedSeqRef = useRef(0);
  const inFlightRef = useRef(false);
  const queuedRef = useRef(false);
  const draftPromiseRef = useRef<Promise<void> | null>(null);
  const timerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  const lockedRef = useRef(false);
  const submittingRef = useRef(false);
  const draftErrorRef = useRef(false);
  const flushRef = useRef<() => void>(() => {});

  useEffect(() => {
    flushRef.current = saveDraft;
  });

  useEffect(() => {
    mountedRef.current = true; // restored on a StrictMode re-mount
    return () => {
      mountedRef.current = false;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      // Flush on unmount, so an edit made within the debounce window before
      // navigating away still saves. Fire-and-forget; mountedRef guards every
      // setState inside.
      flushRef.current();
    };
  }, []);

  /**
   * Adopt `next` as the live form state and reschedule the draft autosave.
   *
   * This is the single write path for user edits and the NPPES prefill, so
   * autosave picks up both the same way.
   */
  const commitForm = (next: FormState): void => {
    formRef.current = next;
    setForm(next);
    setSaved(false);
    editSeqRef.current += 1;
    if (!formEditable) return; // The inputs are disabled when frozen; this guards anyway.
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      saveDraft();
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  const update = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    commitForm({ ...formRef.current, [key]: value });
  };

  /**
   * Save the latest draft now (debounce expiry + the unmount flush). Skips when
   * the form isn't editable, a Submit is running, or nothing new exists; if a
   * draft is already in flight, queues one follow-up instead of overlapping it.
   */
  function saveDraft(): void {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!formEditable || lockedRef.current || submittingRef.current) return;
    if (editSeqRef.current === savedSeqRef.current) return; // nothing new to save
    if (inFlightRef.current) {
      queuedRef.current = true;
      return;
    }
    startDraftPut();
  }

  /** Dispatch the lenient draft PUT. Callers guarantee none is in flight. */
  function startDraftPut(): void {
    inFlightRef.current = true;
    const seq = editSeqRef.current;
    if (mountedRef.current) setAutosaving(true);
    draftPromiseRef.current = api<PartnerOrgProfileResponse>(
      `/api/partner/orgs/${orgId}/profile/draft`,
      {
        method: "PUT",
        body: toDraftBody(formRef.current) as unknown as Record<string, unknown>,
      },
    )
      .then((data) => {
        savedSeqRef.current = seq;
        // Lift the echo so the shell badge and banner stay truthful: a drafted
        // NPI change resets the NPI verdict server-side.
        onSaved(data);
        if (!mountedRef.current) return;
        if (draftErrorRef.current) {
          draftErrorRef.current = false;
          setSaveError(null);
        }
        // Adopt the echo as the new baseline only if nothing was edited while
        // the PUT was in flight; newer keystrokes always win.
        if (editSeqRef.current === seq) {
          const next = toFormState(data);
          formRef.current = next;
          setForm(next);
          setNpiVerification(data.npiVerification);
          setSaved(true);
        }
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          if (err.status === 401) return; // useApi cleared the session; the redirect takes over.
          if (err.status === 409) {
            // Locked mid-session: freeze the form and stop autosaving.
            lockedRef.current = true;
            if (mountedRef.current) setRemoteLocked(true);
            return;
          }
        }
        if (!mountedRef.current) return;
        draftErrorRef.current = true;
        setSaveError("We couldn't save your changes. Please try again.");
      })
      .finally(() => {
        inFlightRef.current = false;
        draftPromiseRef.current = null;
        if (mountedRef.current) setAutosaving(false);
        // A debounce fired mid-flight, so run the deferred draft for the newer
        // state. This runs after unmount too, since it carries a flushed edit;
        // Submit clears the queue before the strict PUT instead.
        if (queuedRef.current) {
          queuedRef.current = false;
          if (
            editSeqRef.current !== savedSeqRef.current &&
            !lockedRef.current &&
            !submittingRef.current
          ) {
            startDraftPut();
          }
        }
      });
  }

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!formEditable || busy || (termsOwed && !termsTicked)) return;

    const legalNameErr = validateLegalName(form.legalName);
    // The NPI is optional, so only a present one is checked. Broker orgs have
    // no NPI; they validate the NPN, licenses and line of authority instead.
    const npiErr = isBroker || form.npi.trim() === "" ? null : validateNpi(form.npi);
    const npnErr = isBroker ? validateNpn(form.npn) : null;
    const licErrs = isBroker ? validateStateLicenses(form.stateLicenses) : NO_LICENSE_ERRORS;
    const licInvalid = licErrs.list !== null || licErrs.rows.some((r) => r !== null);
    const loaMissing = isBroker && form.lineOfAuthority === "";
    const zipErr = validateZip(form.zip);
    const taxIdErr = validateTaxId(combineTaxId(form.taxIdPrefix, form.taxIdSuffix));
    setLegalNameError(legalNameErr);
    setNpiError(npiErr);
    setNpnError(npnErr);
    setLicenseErrors(licErrs);
    setZipError(zipErr);
    setTaxIdError(taxIdErr);
    if (legalNameErr || npiErr || npnErr || licInvalid || zipErr || taxIdErr || loaMissing) {
      // A required select is unset or a field failed validation, so don't
      // round-trip to a 422. orgType is fixed at creation and never a blocker here.
      if (loaMissing) setSaveError("Please fill in every required field.");
      return;
    }

    // Validation passed: open the review prompt before firing the strict PUT.
    // Gating the modal on passing validation, not the raw click, avoids asking
    // the user to confirm a form that would 422.
    setSaveError(null);
    // ZIP ↔ city/state check on the strict submit only, never on the draft
    // autosave; `busy` blocks a second submit while the lookup runs.
    setBusy(true);
    const zipCheck = await checkZipCityState(smartyKey, form);
    setBusy(false);
    if (zipCheck === "mismatch") {
      setZipError(zipMismatchMessage(MESSAGES.en["err.zipMismatch"], form.city, form.state));
      return;
    }
    setConfirmOpen(true);
  };

  // Confirm handler for the pre-submit modal. The overlay blocks form edits, so
  // the values here are the ones onSubmit just validated, and the body is
  // rebuilt from current state rather than stashed. The guard defends against a
  // stale open. Submit supersedes autosave: it cancels the pending debounce and
  // any queued follow-up, blocks new drafts, and waits out an in-flight draft,
  // so a late draft can never clobber the submitted state.
  const handleConfirmSave = (): void => {
    if (!formEditable || busy || (isBroker && form.lineOfAuthority === "")) return;

    setBusy(true);
    setSaveError(null);
    draftErrorRef.current = false;
    submittingRef.current = true;
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    queuedRef.current = false;

    const body: PartnerOrgProfileInput = {
      legalName: form.legalName,
      address: {
        street1: form.street1,
        street2: form.street2,
        city: form.city,
        state: form.state,
        zip: form.zip,
      },
      entityType: form.entityType === "" ? null : form.entityType,
      npi: isBroker ? null : form.npi.trim() || null,
      dea: orgType === "Pharmacy" ? form.dea.trim() || null : null,
      taxId: combineTaxId(form.taxIdPrefix, form.taxIdSuffix),
      // Broker credentials, blank for other org types (the server blanks them
      // there anyway). License numbers are uppercased on save.
      npn: isBroker ? form.npn.trim() : "",
      stateLicenses: isBroker
        ? form.stateLicenses.map((r) => ({
            state: r.state,
            licenseNumber: r.licenseNumber.trim().toUpperCase(),
          }))
        : [],
      lineOfAuthority: isBroker && form.lineOfAuthority !== "" ? form.lineOfAuthority : null,
      // True only while the acceptance is owed; the server then records it in
      // this request. An org that has accepted, or has no terms, sends false.
      termsAccepted: termsOwed && termsTicked,
    };
    const fireStrictPut = (): void => {
      if (lockedRef.current) {
        // The awaited draft came back 409 because the profile just locked. The
        // locked banner is already up, so drop the strict PUT rather than 409 again.
        submittingRef.current = false;
        setBusy(false);
        setConfirmOpen(false);
        return;
      }
      api<PartnerOrgProfileResponse>(`/api/partner/orgs/${orgId}/profile`, {
        method: "PUT",
        body: body as unknown as Record<string, unknown>,
      })
        .then((data) => {
          submittingRef.current = false;
          // The strict PUT persisted every edit, so autosave has nothing left.
          savedSeqRef.current = editSeqRef.current;
          // Reflect the server's computed state so the banner flips without a reload.
          const next = toFormState(data);
          formRef.current = next;
          setForm(next);
          setNpiVerification(data.npiVerification);
          setBusy(false);
          setConfirmOpen(false);
          setSaved(true);
          onSaved(data);
        })
        .catch((err: unknown) => {
          submittingRef.current = false;
          // Close the modal so the in-form error banner below is visible.
          setBusy(false);
          setConfirmOpen(false);
          if (err instanceof ApiError) {
            if (err.status === 401) return; // useApi cleared the session; the redirect takes over.
            if (err.status === 422 && err.detail === TERMS_REQUIRED_DETAIL) {
              // The acceptance was discarded after this page loaded, so the box
              // still reads accepted. Refetch the profile to re-enable the box.
              setSaveError("Please agree to DearBill's Terms & Conditions to submit.");
              api<PartnerOrgProfileResponse>(`/api/partner/orgs/${orgId}/profile`)
                .then(onSaved)
                .catch(() => {}); // The message already says what to do.
              return;
            }
            if (err.status === 422) {
              setSaveError(formatApiErrorDetail(err, "Please check your entries and try again."));
              return;
            }
          }
          setSaveError("We couldn't save your changes. Please try again.");
        });
    };
    // The draft chain never rejects (its .catch swallows), so a bare .then
    // suffices to sequence the strict PUT after an in-flight draft settles.
    const inFlightDraft = draftPromiseRef.current;
    if (inFlightDraft) void inFlightDraft.then(fireStrictPut);
    else fireStrictPut();
  };

  // The Fetch button shows only on an editable form whose NPI passes client
  // validation (10 digits and the Luhn check), so the lookup never 422s.
  const npiFetchable = formEditable && form.npi.trim() !== "" && validateNpi(form.npi) === null;

  /**
   * NPPES prefill: look the NPI up and overwrite the form's legal name, and the
   * address when the record carries one, from the registry record.
   *
   * The three outcomes that populate nothing (outage, no match, individual
   * NPI-1) show a non-blocking inline notice and change nothing.
   */
  const fetchNppes = (): void => {
    if (nppesFetching) return;
    setNppesFetching(true);
    setNppesFetchMessage(null);
    api<PartnerNppesLookupResponse>(
      `/api/partner/orgs/${orgId}/nppes-lookup?npi=${form.npi.trim()}`,
    )
      .then((data) => {
        setNppesFetching(false);
        if (!data.available) {
          setNppesFetchMessage("Couldn't reach the NPI registry. Please try again later.");
          return;
        }
        if (data.resultCount === 0) {
          setNppesFetchMessage("No NPPES record found for this NPI.");
          return;
        }
        if (data.organizationName === null) {
          // An individual NPI-1 record has nothing to prefill an org profile
          // from, even when it carries an address.
          setNppesFetchMessage("This NPI belongs to an individual, not an organization.");
          return;
        }
        // Overwrite from the record. Empty strings in a present address do
        // overwrite, since the fetch mirrors NPPES; a null address leaves the
        // address fields untouched. commitForm runs the same path as typing,
        // so draft autosave picks the values up.
        const next: FormState = { ...formRef.current, legalName: data.organizationName };
        if (data.address !== null) {
          next.street1 = data.address.address1;
          next.street2 = data.address.address2;
          next.city = data.address.city;
          next.state = data.address.state;
          next.zip = normalizeZip(data.address.postalCode);
          setZipError(null);
        }
        commitForm(next);
        setLegalNameError(null);
      })
      .catch((err: unknown) => {
        setNppesFetching(false);
        if (err instanceof ApiError && err.status === 401) return; // useApi cleared the session.
        setNppesFetchMessage("Couldn't reach the NPI registry. Please try again later.");
      });
  };

  // Active org, not editing: the design's read-only summary state. The Edit
  // button flips to the form below; a plain re-render (status still active)
  // flips back only through the form's Done button.
  if (showSummary) {
    return (
      <OrgSummary
        res={res}
        termsDoc={termsDoc}
        termsDocError={termsDocError}
        onEdit={() => setEditing(true)}
      />
    );
  }

  return (
    <>
      <div className="mb-5">
        <h2 className="text-foreground font-serif text-[34px] font-semibold leading-[42px]">
          Organization Profile
        </h2>
        <p className="text-foreground mt-1.5 text-[17px] leading-6">
          What DearBill has on file for your organization.
        </p>
        {/* The way back to the summary exists only when the summary exists
            (an active org mid-edit). */}
        {res.status === "active" && (
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-ember hover:text-ember-dark mt-2 cursor-pointer text-[15px] font-semibold underline underline-offset-2"
          >
            Back to the profile summary
          </button>
        )}
      </div>

      {!canEdit && (
        <p className="border-haze text-slate-teal mb-6 rounded-lg border bg-white px-4 py-3 text-[15px] leading-[22px]">
          Only an organization admin can edit this profile.
        </p>
      )}

      {/* Profile-edit lock notice, shown only to an org-admin, since a `user`
          already sees the read-only notice above. The copy gives no reason
          because an admin can lock any org manually. `locked` also covers a
          lock detected live through a draft 409. */}
      {canEdit && locked && (
        <p
          role="status"
          className="border-haze bg-lemon text-ochre mb-6 rounded-lg border px-4 py-3 text-[15px] leading-[22px]"
        >
          This organization&apos;s profile is locked and can&apos;t be edited. Contact DearBill to
          make changes.
        </p>
      )}

      <section className={cn(CARD, "max-w-3xl")}>
        <h3 className={CARD_HEADING}>Organization profile</h3>
        <form className="mt-4 space-y-5" onSubmit={(e) => void onSubmit(e)} noValidate>
          {/* The organization type is set at creation and immutable, so it
            shows read-only. */}
          <Input label="Organization type" value={orgType} disabled />

          {/* NPI leads the form so the Fetch button beside a valid NPI can
            prefill the legal name and address below from the NPPES registry.
            Hidden for a Broker, which has no NPI. */}
          {!isBroker && (
            <>
              <div className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <Input
                    label="NPI"
                    hint="Optional — your organization's 10-digit National Provider Identifier, the number on your NPPES registration and Medicare claims."
                    labelAddon={res.nppesBypass ? <NpiTestHelp /> : undefined}
                    value={form.npi}
                    disabled={!formEditable}
                    error={npiError ?? undefined}
                    onChange={(e) => {
                      update("npi", e.target.value);
                      setNpiError(null);
                    }}
                  />
                </div>
                {npiFetchable && (
                  <button
                    type="button"
                    onClick={fetchNppes}
                    disabled={nppesFetching}
                    className={cn(BTN_SECONDARY, "min-h-12")}
                  >
                    {nppesFetching ? "Fetching…" : "Fetch"}
                  </button>
                )}
              </div>
              {nppesFetchMessage && (
                <p role="status" className={STATUS_WARN}>
                  {nppesFetchMessage}
                </p>
              )}
              <NpiVerificationBanner verification={npiVerification} />
            </>
          )}

          {/* Broker credentials (NPN, line of authority, licensed states) lead
            the form in place of the NPI block. All three are required on the
            strict Submit, and the draft autosave covers every edit like the rest
            of the form, since the editor's onChange goes through `update`. */}
          {isBroker && (
            <>
              <Input
                label="NPN"
                required
                inputMode="numeric"
                hint="Your National Producer Number (up to 10 digits) — printed on your insurance producer license."
                value={form.npn}
                disabled={!formEditable}
                error={npnError ?? undefined}
                onChange={(e) => {
                  update("npn", e.target.value);
                  setNpnError(null);
                }}
              />
              <Select
                label="Line of Authority"
                required
                hint="The kind of insurance your license lets you sell — shown on your producer license."
                value={form.lineOfAuthority}
                disabled={!formEditable}
                onChange={(e) => update("lineOfAuthority", e.target.value as LineOfAuthority | "")}
              >
                <option value="">Select…</option>
                {LINES_OF_AUTHORITY.map((loa) => (
                  <option key={loa} value={loa}>
                    {loa}
                  </option>
                ))}
              </Select>
              <StateLicensesEditor
                value={form.stateLicenses}
                disabled={!formEditable}
                rowErrors={licenseErrors.rows}
                error={licenseErrors.list ?? undefined}
                onChange={(rows) => {
                  update("stateLicenses", rows);
                  setLicenseErrors(NO_LICENSE_ERRORS);
                }}
              />
            </>
          )}

          <Input
            label="Legal business name"
            required
            value={form.legalName}
            disabled={!formEditable}
            error={legalNameError ?? undefined}
            onChange={(e) => {
              update("legalName", e.target.value);
              setLegalNameError(null);
            }}
          />

          <Select
            label="Entity type"
            hint="Optional — how your organization is legally structured."
            value={form.entityType}
            disabled={!formEditable}
            onChange={(e) => update("entityType", e.target.value as EntityType)}
          >
            <option value="">Select…</option>
            {ENTITY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>

          {/* Tax ID: two digit-only boxes with a literal "-" between, combined
            on the wire as `XX-XXXXXXX`. Optional, but a partly filled ID fails
            validation. */}
          <div>
            <FieldLabel
              htmlFor="tax-id-prefix"
              hint="Optional — your federal Employer Identification Number (EIN), from your IRS paperwork: 2 digits, then 7."
            >
              Tax ID
            </FieldLabel>
            <div className="flex items-center gap-2">
              <input
                id="tax-id-prefix"
                aria-label="Tax ID prefix"
                inputMode="numeric"
                maxLength={2}
                className={cn(
                  "w-16",
                  TAX_ID_BOX_CLASSES,
                  taxIdError && "border-danger/40 bg-danger-bg focus:border-danger",
                )}
                value={form.taxIdPrefix}
                disabled={!formEditable}
                aria-invalid={taxIdError ? true : undefined}
                onChange={(e) => {
                  update("taxIdPrefix", e.target.value.replace(/\D/g, "").slice(0, 2));
                  setTaxIdError(null);
                }}
              />
              <span aria-hidden="true" className="text-slate-teal">
                -
              </span>
              <input
                aria-label="Tax ID suffix"
                inputMode="numeric"
                maxLength={7}
                className={cn(
                  "w-40",
                  TAX_ID_BOX_CLASSES,
                  taxIdError && "border-danger/40 bg-danger-bg focus:border-danger",
                )}
                value={form.taxIdSuffix}
                disabled={!formEditable}
                aria-invalid={taxIdError ? true : undefined}
                onChange={(e) => {
                  update("taxIdSuffix", e.target.value.replace(/\D/g, "").slice(0, 7));
                  setTaxIdError(null);
                }}
              />
            </div>
            {taxIdError && (
              <p role="alert" className="text-danger mt-1 text-xs">
                {taxIdError}
              </p>
            )}
          </div>

          <AddressAutocompleteInput
            label="Street address"
            required
            value={form.street1}
            disabled={!formEditable}
            embeddedKey={smartyKey}
            onChange={(e) => update("street1", e.target.value)}
            onSelect={(s: AddressSuggestion) => {
              // One commit through the single write path, so the draft
              // autosave fires as it does for the NPPES prefill.
              commitForm({
                ...formRef.current,
                street1: s.street1,
                city: s.city,
                state: s.state,
                zip: s.zip,
              });
              setZipError(null);
            }}
          />
          <Input
            label="Apt, suite, etc."
            value={form.street2}
            disabled={!formEditable}
            onChange={(e) => update("street2", e.target.value)}
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <Input
              label="City"
              required
              value={form.city}
              disabled={!formEditable}
              onChange={(e) => update("city", e.target.value)}
            />
            <Select
              label="State"
              required
              value={form.state}
              disabled={!formEditable}
              onChange={(e) => update("state", e.target.value)}
            >
              <option value="">Select…</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code}
                </option>
              ))}
            </Select>
            <Input
              label="ZIP"
              required
              value={form.zip}
              disabled={!formEditable}
              error={zipError ?? undefined}
              onChange={(e) => {
                update("zip", e.target.value);
                setZipError(null);
              }}
            />
          </div>

          {orgType === "Pharmacy" && (
            <Input
              label="DEA"
              hint="Optional — your pharmacy's DEA registration number, from your DEA certificate."
              value={form.dea}
              disabled={!formEditable}
              onChange={(e) => update("dea", e.target.value)}
            />
          )}

          {/* Org-admin only, like the acceptance date's endpoint. A locked
                form keeps the box disabled, so an org locked before it
                accepted needs an admin unlock first. */}
          {canEdit && res.agreementRequired && (
            <div>
              <Checkbox
                label={<TermsAgreementLabel linked={orgType === "Pharmacy"} />}
                required
                checked={termsAccepted || termsTicked}
                disabled={termsAccepted || !formEditable || busy}
                aria-describedby={termsDoc?.signed ? "terms-accepted-at" : undefined}
                onChange={(e) => setTermsTicked(e.target.checked)}
              />
              {termsAccepted && termsDoc?.signed && (
                <p id="terms-accepted-at" className="text-slate-teal ml-8 mt-1 text-sm leading-5">
                  Accepted {fmtDate(termsDoc.signedAt)}
                </p>
              )}
            </div>
          )}

          {saveError && <Alert tone="error" title={saveError} />}

          {formEditable && (
            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={busy || (termsOwed && !termsTicked)}
                className={BTN_PRIMARY}
              >
                {busy ? "Submitting…" : "Submit"}
              </button>
              {autosaving && !busy && (
                <span role="status" className="text-slate-teal text-sm">
                  Saving…
                </span>
              )}
              {saved && !busy && !autosaving && (
                <span role="status" className="text-success text-sm">
                  Saved.
                </span>
              )}
            </div>
          )}
        </form>
      </section>

      {/* Pre-submit review prompt, reachable only once the form is editable and
          client validation has passed. Confirm runs the strict PUT; Cancel
          closes with no request. */}
      <ConfirmModal
        open={confirmOpen && formEditable}
        title="Submit your organization profile?"
        confirmLabel="Confirm and submit"
        busyLabel="Submitting…"
        busy={busy}
        onConfirm={handleConfirmSave}
        onCancel={() => setConfirmOpen(false)}
      >
        <p>
          Please make sure all of your organization&apos;s information is correct. Your profile will
          be locked for DearBill to review when you submit — after that you&apos;ll need to contact
          DearBill to make any changes.
        </p>
      </ConfirmModal>
    </>
  );
}

/**
 * Static dev fixture: the test NPIs the NPPES bypass accepts and the org legal
 * name each resolves to.
 *
 * Shown as an expandable "?" next to the NPI label only when the org-profile
 * response reports `nppesBypass: true` (the NPPES_BYPASS feature flag is on),
 * so a tester knows which NPIs the stub recognizes.
 */
/** Uppercase field label of the summary card's detail grid. */
const SUMMARY_LABEL =
  "text-slate-teal text-[13px] font-semibold uppercase leading-[18px] tracking-[0.8px]";

/**
 * Read-only profile summary for an active org (Figma dR8cJ6hRSW5eERToo64boe
 * node 2340:769).
 *
 * Left column: the org card (name, approved-to-refer marker, the credential
 * grid) and, for an org_admin of a Pharmacy or Broker org, the Documents on
 * file card. Right column: the Owner card from `res.owner`. Fields with no
 * backend are left out rather than faked: there is no state-pharmacy-license,
 * fax, privacy-agreement or second-contact storage.
 *
 * The Edit button hands control back to the parent, which swaps in the form;
 * a `user` gets "View details" instead, since their form is read-only.
 */
function OrgSummary({
  res,
  termsDoc,
  termsDocError,
  onEdit,
}: {
  res: PartnerOrgProfileResponse;
  /** The parent's Terms & Conditions fetch, for the Documents on file card. */
  termsDoc: PartnerDocumentStatus | null;
  termsDocError: string | null;
  onEdit: () => void;
}): ReactNode {
  const { profile } = res;
  const isBroker = profile.organizationType === "Broker";
  const canEdit = res.role === "org_admin";

  const addressLine2 = [profile.address.city, profile.address.state]
    .filter((part) => part !== "")
    .join(", ");

  return (
    <>
      <div className="mb-5">
        <h2 className="text-foreground font-serif text-[34px] font-semibold leading-[42px]">
          Organization Profile
        </h2>
        <p className="text-foreground mt-1.5 text-[17px] leading-6">
          If a license or an agreement lapses, new referrals will pause until documents are updated.
          The calculator will remain active.
        </p>
      </div>

      <div className="flex flex-col items-start gap-5 lg:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-5">
          <section className="border-border-strong rounded-2xl border bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5">
              <div>
                <h3 className={CARD_HEADING}>{res.name}</h3>
                <p className="text-success mt-1 flex items-center gap-1.5 text-[15px] font-semibold leading-[22px]">
                  Approved to refer patients
                  <CheckCircleIcon size={18} className="shrink-0" />
                </p>
              </div>
              <button type="button" onClick={onEdit} className={BTN_SECONDARY}>
                {canEdit ? "Edit" : "View details"}
              </button>
            </div>
            <div className="border-haze grid grid-cols-1 gap-6 border-t px-6 py-5 sm:grid-cols-2">
              {/* Credentials: the NPI with its registry verdict, or the broker
                  NPN with the line of authority. */}
              {!isBroker ? (
                <div>
                  <p className={SUMMARY_LABEL}>{profile.organizationType} NPI</p>
                  <p className="text-foreground mt-1 font-mono text-[19px] font-medium leading-[26px]">
                    {profile.npi || "—"}
                  </p>
                  {res.npiVerification.status === "verified" && (
                    <p className="text-success mt-0.5 text-sm leading-5">
                      Matches the federal registry
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <p className={SUMMARY_LABEL}>NPN</p>
                  <p className="text-foreground mt-1 font-mono text-[19px] font-medium leading-[26px]">
                    {profile.npn || "—"}
                  </p>
                  {profile.lineOfAuthority !== null && (
                    <p className="text-slate-teal mt-0.5 text-sm leading-5">
                      {profile.lineOfAuthority}
                    </p>
                  )}
                </div>
              )}
              {/* The license cell: a pharmacy's DEA registration, or a broker's
                  state licenses. A provider has neither, so the cell is left
                  out for one. */}
              {profile.organizationType === "Pharmacy" && profile.dea !== "" && (
                <div>
                  <p className={SUMMARY_LABEL}>DEA registration</p>
                  <p className="text-foreground mt-1 font-mono text-[19px] font-medium leading-[26px]">
                    {profile.dea}
                  </p>
                </div>
              )}
              {isBroker && profile.stateLicenses.length > 0 && (
                <div>
                  <p className={SUMMARY_LABEL}>State licenses</p>
                  {profile.stateLicenses.map((lic) => (
                    <p
                      key={`${lic.state}-${lic.licenseNumber}`}
                      className="text-foreground mt-1 font-mono text-[17px] leading-6"
                    >
                      {lic.state} {lic.licenseNumber}
                    </p>
                  ))}
                </div>
              )}
              <div>
                <p className={SUMMARY_LABEL}>Address</p>
                <p className="text-foreground mt-1 text-[17px] leading-6">
                  {profile.address.street1 || "—"}
                  {profile.address.street2 !== "" && `, ${profile.address.street2}`}
                </p>
                {addressLine2 !== "" && (
                  <p className="text-foreground text-[17px] leading-6">
                    {addressLine2} {profile.address.zip}
                  </p>
                )}
              </div>
              <div>
                <p className={SUMMARY_LABEL}>Phone</p>
                <p className="text-foreground mt-1 font-mono text-[17px] font-medium leading-6">
                  {profile.phone !== "" ? formatUsPhone(profile.phone) : "—"}
                </p>
              </div>
            </div>
          </section>

          {/* Documents on file: org_admin only, because the document-status
              endpoint is admin-gated, and only when the Terms & Conditions
              apply. There is no stored privacy agreement, so no second row. */}
          {canEdit && res.agreementRequired && (
            <SummaryDocumentsCard
              doc={termsDoc}
              loadError={termsDocError}
              linked={profile.organizationType === "Pharmacy"}
            />
          )}
        </div>

        <section className="border-border-strong w-full shrink-0 rounded-2xl border bg-white p-6 lg:w-[456px]">
          <h3 className={CARD_HEADING}>Owner</h3>
          <p className="text-foreground mt-2 text-lg font-semibold leading-[26px]">
            {res.owner?.name ?? "—"}
          </p>
          {res.owner !== null && res.owner.phone !== "" && (
            <p className="text-foreground font-mono text-base font-medium leading-6">
              {formatUsPhone(res.owner.phone)}
            </p>
          )}
          {res.owner !== null && (
            <p className="text-foreground text-base leading-6">{res.owner.email}</p>
          )}
        </section>
      </div>
    </>
  );
}

/**
 * Read the org's Terms & Conditions acceptance (`GET .../document`) while
 * `enabled`. The endpoint is org-admin only, so callers fold the role into
 * `enabled`.
 */
function useTermsAcceptance(
  orgId: string,
  enabled: boolean,
): { doc: PartnerDocumentStatus | null; loadError: string | null } {
  const api = useApi();
  const [doc, setDoc] = useState<PartnerDocumentStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    setDoc(null);
    setLoadError(null);
    if (!enabled) return;
    let cancelled = false;
    api<PartnerDocumentStatus>(`/api/partner/orgs/${orgId}/document`)
      .then((data) => {
        if (!cancelled) setDoc(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        setLoadError("We couldn't load your documents. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [api, orgId, enabled]);

  return { doc, loadError };
}

/**
 * The summary state's "Documents on file" card: one Terms & Conditions row
 * with its acceptance date. For a Pharmacy org (`linked`) the row's title
 * opens the committed Pharmacy Partner Terms PDF in a new tab; a Broker org's
 * row stays plain text, since the document names a Pharmacy as its
 * counterparty. The signer's name isn't stored, so the line carries the date
 * only.
 */
function SummaryDocumentsCard({
  doc,
  loadError,
  linked,
}: {
  doc: PartnerDocumentStatus | null;
  loadError: string | null;
  linked: boolean;
}): ReactNode {
  return (
    <section className="border-border-strong rounded-2xl border bg-white">
      <h3 className={cn(CARD_HEADING, "px-6 py-4")}>Documents on file</h3>
      {loadError && (
        <p role="alert" className="text-danger border-haze border-t px-6 py-4 text-sm">
          {loadError}
        </p>
      )}
      {doc === null && !loadError && (
        <div className="text-slate-teal border-haze flex items-center gap-3 border-t px-6 py-4 text-sm">
          <Spinner /> <span>Loading documents…</span>
        </div>
      )}
      {doc !== null && (
        <div className="border-haze border-t px-6 py-4">
          <p className="text-foreground text-lg font-semibold leading-[26px]">
            {linked ? (
              <a
                href={PHARMACY_PARTNER_TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-text hover:text-ember-dark underline"
              >
                Terms &amp; Conditions
              </a>
            ) : (
              <>Terms &amp; Conditions</>
            )}
          </p>
          <p className="text-slate-teal text-[15px] leading-[22px]">
            {doc.signed ? `Accepted ${fmtDate(doc.signedAt)}` : "Not accepted yet"}
          </p>
        </div>
      )}
    </section>
  );
}

// ponytail: a hand copy of `_SYNTHETIC_NPPES` in
// server/app/services/npi_verification.py, so it drifts if that table changes.
const TEST_NPIS: { npi: string; name: string }[] = [
  { npi: "1122334455", name: "DEARBILL TEST PHARMACY LLC" },
  { npi: "1000000004", name: "DEARBILL SANDBOX CLINIC" },
  { npi: "1999999992", name: "DEARBILL DEACTIVATED CLINIC" },
  { npi: "1555555550", name: "JANE Q TESTPROVIDER" },
  { npi: "1444444446", name: "DEARBILL FAMILY CLINIC" },
];

/**
 * Expandable "?" next to the NPI label, rendered through the Input's
 * `labelAddon`. A native `<details>` with no JS state lists the accepted test
 * NPIs; the `?` is its summary.
 */
function NpiTestHelp(): ReactNode {
  return (
    <details className="relative inline-block text-left">
      <summary
        className="border-haze text-slate-teal hover:border-haze hover:text-foreground flex h-5 w-5 cursor-pointer list-none items-center justify-center rounded-full border text-xs font-semibold [&::-webkit-details-marker]:hidden"
        aria-label="Show accepted test NPIs"
      >
        ?
      </summary>
      <div className="border-haze text-slate-teal absolute z-10 mt-2 w-72 rounded-lg border bg-white p-3 text-xs shadow-lg">
        <p className="text-foreground mb-2 font-medium">Accepted test NPIs (NPPES bypass is on):</p>
        <dl className="space-y-1">
          {TEST_NPIS.map(({ npi, name }) => (
            <div key={npi} className="flex items-baseline justify-between gap-3">
              <dt className="text-foreground">{npi}</dt>
              <dd className="text-slate-teal text-right">{name}</dd>
            </div>
          ))}
        </dl>
      </div>
    </details>
  );
}

/**
 * Soft, non-blocking NPI-verification status.
 *
 * Informational only: the save succeeded server-side whatever the verdict, and
 * this never blocks the form. Renders nothing while `not_run`.
 */
function NpiVerificationBanner({ verification }: { verification: NpiVerification }): ReactNode {
  if (verification.status === "verified") {
    return (
      <p role="status" className={STATUS_SUCCESS}>
        NPI verified. Matches the federal registry.
      </p>
    );
  }
  if (verification.status === "flagged" || verification.status === "unavailable") {
    return (
      <p role="status" className={STATUS_WARN}>
        We couldn&apos;t fully verify this NPI — you can continue; our team will review.
      </p>
    );
  }
  return null;
}
